import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, Send, Image as ImageIcon, X, Loader2, Users, Trash2,
  Check, Search, ChevronDown, ChevronUp, User2,
} from "lucide-react";
import { Broadcasts, UsersAPI } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmProvider";

/**
 * AdminBroadcasts — compose + history of admin → user announcements.
 *
 * Layout:
 *   Top   = composer (title, body, image, recipient picker, send)
 *   Below = history table (title, audience, ack count, sent_at, actions)
 */
export default function AdminBroadcasts() {
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [h, u] = await Promise.all([
        Broadcasts.adminList(),
        UsersAPI.list().catch(() => []),
      ]);
      setHistory(h.broadcasts || []);
      setUsers(u || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie mogę pobrać komunikatów");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-3 md:p-6 space-y-4" data-testid="admin-broadcasts-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          // Panel admina · Komunikaty
        </div>
        <h1 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mt-1">
          Komunikaty do użytkowników
        </h1>
        <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
          Wyślij komunikat do wybranych osób lub wszystkich użytkowników. Odbiorcy
          zobaczą pop-up po zalogowaniu (z dźwiękiem powiadomienia) i muszą go
          potwierdzić klikając &bdquo;Potwierdzam odczyt&rdquo;.
        </p>
      </div>

      <Composer users={users} onSent={load} />

      <HistoryTable
        loading={loading}
        rows={history}
        onChanged={load}
        userIndex={users}
      />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Composer
// ═════════════════════════════════════════════════════════════════════════
function Composer({ users, onSent }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null); // data-url or null
  const [mode, setMode] = useState("all"); // "all" | "select"
  const [selected, setSelected] = useState(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (!q) return true;
      const hay = [u.name, u.email, u.username, u.role].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [users, query]);

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Wybierz plik obrazu (PNG/JPG/WEBP)");
      return;
    }
    if (f.size > 250_000) {
      toast.error("Obraz jest za duży — max 250 KB. Zmniejsz go i spróbuj ponownie.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
  };

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const send = async () => {
    if (!title.trim()) return toast.error("Podaj tytuł komunikatu");
    if (mode === "select" && selected.size === 0) {
      return toast.error("Wybierz co najmniej jednego odbiorcę lub przełącz na „Wszyscy\"");
    }
    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        body,
        image_base64: image || null,
        recipients: mode === "all" ? "all" : Array.from(selected),
        require_ack: true,
      };
      const r = await Broadcasts.adminSend(payload);
      toast.success(`Wysłano do ${r.audience} użytkowników`);
      // Reset
      setTitle("");
      setBody("");
      setImage(null);
      setSelected(new Set());
      setMode("all");
      setPickerOpen(false);
      if (fileRef.current) fileRef.current.value = "";
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd wysyłki");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
      <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
        <Megaphone className="w-4 h-4 text-orange-600" />
        <div className="text-xs font-bold uppercase tracking-widest text-zinc-800">
          Nowy komunikat
        </div>
      </div>
      <div className="p-4 space-y-3">
        <input
          data-testid="bc-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder='Tytuł komunikatu (np. „Nowe zlecenie na jutro")'
          className="w-full px-3 py-2 rounded-xl ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm"
        />
        <textarea
          data-testid="bc-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          placeholder="Treść (opcjonalnie)"
          className="w-full px-3 py-2 rounded-xl ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm"
        />

        {/* Image picker */}
        <div>
          {image ? (
            <div className="relative inline-block">
              <img
                src={image}
                alt="Podgląd"
                className="max-h-40 rounded-xl ring-1 ring-zinc-200"
              />
              <button
                type="button"
                onClick={() => { setImage(null); if (fileRef.current) fileRef.current.value = ""; }}
                data-testid="bc-image-remove"
                className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg hover:bg-red-600"
                title="Usuń obraz"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-xs font-bold uppercase tracking-widest text-zinc-700 cursor-pointer">
              <ImageIcon className="w-4 h-4" /> Dodaj obraz (opcjonalnie)
              <input
                ref={fileRef}
                data-testid="bc-image-input"
                type="file"
                accept="image/*"
                onChange={onFilePick}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Recipient selector */}
        <div className="rounded-xl ring-1 ring-zinc-200 overflow-hidden">
          <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
            <Users className="w-4 h-4 text-zinc-500" />
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-800">
              Odbiorcy
            </div>
          </div>
          <div className="p-3 flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                data-testid="bc-mode-all"
                checked={mode === "all"}
                onChange={() => setMode("all")}
                className="accent-orange-500"
              />
              <span className="text-sm font-semibold">Wszyscy użytkownicy ({users.length})</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                data-testid="bc-mode-select"
                checked={mode === "select"}
                onChange={() => setMode("select")}
                className="accent-orange-500"
              />
              <span className="text-sm font-semibold">
                Wybrani ({selected.size} zaznaczonych)
              </span>
            </label>

            {mode === "select" && (
              <div className="mt-1 rounded-xl bg-zinc-50 ring-1 ring-zinc-200">
                <button
                  type="button"
                  onClick={() => setPickerOpen((v) => !v)}
                  data-testid="bc-toggle-picker"
                  className="w-full px-3 py-2 flex items-center gap-2 hover:bg-zinc-100 text-xs font-bold uppercase tracking-widest text-zinc-700"
                >
                  <User2 className="w-3.5 h-3.5" />
                  {pickerOpen ? "Zamknij listę" : "Otwórz listę użytkowników"}
                  {pickerOpen ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                </button>
                {pickerOpen && (
                  <div className="border-t border-zinc-200">
                    <div className="p-2 relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
                      <input
                        data-testid="bc-user-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filtruj po imieniu, emailu, roli…"
                        className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-white ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 text-xs"
                      />
                    </div>
                    <div className="max-h-52 overflow-y-auto divide-y divide-zinc-100">
                      {filteredUsers.length === 0 ? (
                        <div className="p-4 text-center text-xs text-zinc-500">Brak dopasowań.</div>
                      ) : filteredUsers.map((u) => {
                        const isChecked = selected.has(u.user_id);
                        return (
                          <button
                            key={u.user_id}
                            type="button"
                            onClick={() => toggle(u.user_id)}
                            data-testid={`bc-user-${u.user_id}`}
                            className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-zinc-50 ${isChecked ? "bg-emerald-50" : ""}`}
                          >
                            <div className={`w-4 h-4 rounded ring-1 flex items-center justify-center shrink-0 ${
                              isChecked ? "bg-emerald-500 ring-emerald-600" : "bg-white ring-zinc-300"
                            }`}>
                              {isChecked && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold truncate">
                                {u.name || u.email || u.username || u.user_id}
                              </div>
                              <div className="text-[10px] font-mono text-zinc-500">
                                {u.role || "—"} · {u.email || u.username || "—"}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={send}
            disabled={sending || !title.trim()}
            data-testid="bc-send"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-heading font-black uppercase tracking-widest transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Wysyłam…" : "Wyślij komunikat"}
          </button>
          <span className="text-[10px] font-mono text-zinc-400">
            // Wszyscy odbiorcy zobaczą pop-up z dźwiękiem i muszą go potwierdzić.
          </span>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// History
// ═════════════════════════════════════════════════════════════════════════
function HistoryTable({ loading, rows, onChanged, userIndex }) {
  const { confirm } = useConfirm();
  const userNameById = useMemo(() => {
    const m = {};
    (userIndex || []).forEach((u) => {
      m[u.user_id] = u.name || u.email || u.username || u.user_id;
    });
    return m;
  }, [userIndex]);

  const remove = async (bc) => {
    if (!(await confirm(
      `Usunąć komunikat „${bc.title}"? Historyczne potwierdzenia zostaną zachowane.`,
      { tone: "warn", confirmLabel: "Usuń" },
    ))) return;
    try {
      await Broadcasts.adminDelete(bc.id);
      toast.success("Usunięto");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd usuwania");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Ładuję historię…
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
      <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
        <div className="text-xs font-bold uppercase tracking-widest text-zinc-800">
          Historia wysyłek ({rows.length})
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-zinc-500">
          Brak wysłanych komunikatów. Utwórz pierwszy powyżej.
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {rows.map((r) => {
            const audience = r.recipients === "all"
              ? "Wszyscy"
              : Array.isArray(r.recipients)
              ? `${r.recipients.length} osoby`
              : "—";
            return (
              <div key={r.id} className={`p-3 flex items-start gap-3 ${r.deleted_at ? "opacity-50" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-heading font-black uppercase tracking-tight truncate">
                      {r.title}
                    </div>
                    {r.deleted_at && (
                      <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                        Usunięty
                      </span>
                    )}
                  </div>
                  {r.body && (
                    <div className="text-xs text-zinc-600 mt-1 line-clamp-2 whitespace-pre-wrap">
                      {r.body}
                    </div>
                  )}
                  <div className="text-[10px] font-mono text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{new Date(r.created_at).toLocaleString("pl-PL")}</span>
                    <span>·</span>
                    <span>{r.created_by_name || "admin"}</span>
                    <span>·</span>
                    <span>{audience}</span>
                    <span>·</span>
                    <span className="text-emerald-700">{r.ack_count} potwierdzeń</span>
                  </div>
                  {Array.isArray(r.recipients) && r.recipients.length > 0 && r.recipients.length <= 12 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {r.recipients.map((uid) => (
                        <span key={uid} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-100 text-[10px] font-mono text-zinc-700">
                          <User2 className="w-2.5 h-2.5" />
                          {userNameById[uid] || uid.slice(0, 8)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {!r.deleted_at && (
                  <button
                    onClick={() => remove(r)}
                    data-testid={`bc-delete-${r.id}`}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 shrink-0"
                    title="Usuń komunikat"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
